import { eq } from '@tanstack/db'
import { useLiveQuery } from '@tanstack/react-db'
import { errorToString } from '@/lib/errors'
import { performMutations, useMutation } from '@/lib/mutations'
import { notify } from '@/lib/notice'
import { useStore } from '@/lib/pocketbase'
import { buildUpdatePayload, type OrgSettingsFormData } from '@/stores/org-settings-store'
import type { Orgs } from '@/types/pbSchema'
import { useNavigateBack } from './useNavigateBack'

export function useOrgSettings(orgSlug: string) {
    const [orgsCollection] = useStore('orgs')

    const { data: orgs = [], isLoading } = useLiveQuery(
        q =>
            orgSlug
                ? q.from({ org: orgsCollection }).where(({ org }) => eq(org.slug, orgSlug))
                : undefined,
        [orgSlug]
    )

    const settings = orgs[0] ?? null

    return { settings, isLoading }
}

interface UpdateOrgSettingsParams {
    data: OrgSettingsFormData
    orgSlug: string
    onSaved?: () => void
}

export function useUpdateOrgSettings(settings: Orgs | null) {
    const [orgsCollection] = useStore('orgs')
    const { navigateBackTo } = useNavigateBack()

    return useMutation({
        mutationFn: async ({ data }: UpdateOrgSettingsParams) => {
            const payload = buildUpdatePayload(data)

            if (!settings?.id) {
                throw new Error('Organization not found')
            }

            await performMutations(function* () {
                yield orgsCollection.update(settings.id, draft => {
                    Object.assign(draft, payload)
                })
            })

            return { orgSlug: data }
        },
        onSuccess: (_, { orgSlug: returnedSlug, onSaved }) => {
            notify.success('Organization settings updated')
            // Run caller's post-save hook (e.g. form reset) BEFORE navigating
            // so usePreventRemove sees a clean form.
            onSaved?.()
            navigateBackTo(`/${returnedSlug}/settings/`)
        },
        onError: error => {
            notify.error(errorToString(error))
        },
    })
}
